import DesignTokens from '@tableau/design-tokens';
import { TabStyles } from '@tableau/widgets';
import { CSSProperties } from 'react';
import { media, style } from 'typestyle';
import { NestedCSSProperties } from 'typestyle/lib/types';

import { CommonStyles } from '~src/commonInfra/styles/CommonStyles';
import { applyColor }from '~src/style/ColorService';

import { BreakPoints } from '../../../starters/styles/StarterStyles';
import { cssClass, Style } from '../../../style';
import { ExplorerStyles } from '../Explorer/ExplorerStyles';
import { SpacesCommonStyles } from '../spaceComponents/SpacesStyles';

export const EDITABLE_NAME_ROW_HEIGHT = 42;

const cssPrefix = 'SpaceHeaderStyles';

function buildCss(className: string, ...properties: Style[]): string {
  return cssClass(cssPrefix, className, ...properties);
}

const row: NestedCSSProperties = {
  display: 'flex',
  flexDirection: 'row',
  $nest: {
    '&>*': {
      marginRight: TabStyles.Spacing.MediumSpace
    },
    '&>*:last-child': {
      marginRight: 0
    }
  },
};

const rowV2: NestedCSSProperties = {
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  alignItems: 'center',
  $nest: {
    '&>*': {
      marginRight: CommonStyles.Sizes.XLargeSpace
    },
    '&>*:last-child': {
      marginRight: 0
    }
  },
};

const grow: CSSProperties = {
  flex: '1 0 auto'
};

const rowNowrap: NestedCSSProperties = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  maxWidth: '100%',
  $nest: {
    '&>*': {
      marginRight: TabStyles.Spacing.MediumSpace
    },
    '&>*:last-child': {
      marginRight: 0
    }
  },
};

const column: NestedCSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  $nest: {
    '&>*': {
      marginBottom: TabStyles.Spacing.DefaultSpace
    }
  }
};

export const truncated: React.CSSProperties = {
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  minWidth: 0
};

const center: NestedCSSProperties = {
  alignItems: 'center'
};

const MetadataStyles = {
  color: applyColor(DesignTokens.color.font.default.secondary, CommonStyles.Colors.Grey102),
  marginRight: TabStyles.Spacing.DefaultSpace
};

const noWrapText: CSSProperties = {
  whiteSpace: 'nowrap',
};

export namespace SpaceHeaderStyles {
  export const Row = buildCss('row', row);
  export const RowV2 = buildCss('row', rowV2);
  export const RowNowrap = buildCss('row_nowrap', rowNowrap);
  export const RowAlignedCenter = buildCss('row_center', row, center, truncated);
  export const Column = buildCss('column', column, truncated);
  export const Grow = buildCss('grow', grow);

  const capWidth: CSSProperties = {
    maxWidth: '100%'
  };

  export const CapWidth = buildCss('cap_width', capWidth);

  export const ColumnFlexGrow = cssClass(cssPrefix, 'row-column-grow', {
    ...column,
    ...truncated,
    flexGrow: 1
  });

  const MastheadIconWidthPx = '60px';
  export const IconDivWrapper = cssClass(cssPrefix, 'icon-div-wrapper', {
    minWidth: MastheadIconWidthPx,
    height: '60px',
    position: 'relative'
  });

  export const IconDivWrapperV2 = cssClass(cssPrefix, 'icon-div-wrapper-v2', {
    minWidth: MastheadIconWidthPx,
    height: '60px'
  });


  const IconStyle: React.CSSProperties = {
    height: '60px',
    width: MastheadIconWidthPx,
    position: 'absolute',
    // This vertically aligns it with the owner text
    top: '-5px',
    left: 0,
  };

  export const Icon = cssClass(cssPrefix, 'icon', {
    ...IconStyle
  });

  export const CollectionIcon = cssClass(cssPrefix, 'icon', {
    ...IconStyle,
    top: '0px',
  });

  export const SiteUserIcon = cssClass(cssPrefix, 'site-usericon', {
    height: '60px',
    width: MastheadIconWidthPx,
    position: 'absolute',
    // This vertically aligns it with the owner text
    top: '-5px',
    left: 0,
    borderRadius: '50%',
    objectFit: 'cover'
  });

  export const TitleCSSV2: React.CSSProperties = {
    fontWeight: 'normal',
    fontSize: SpacesCommonStyles.TitleFontSize,
    margin: '0',
    marginRight: TabStyles.Spacing.ExtraLargeSpace,
    height: SpacesCommonStyles.RowHeight,
    lineHeight: SpacesCommonStyles.RowHeight,
    flex: '1 0 auto',
    textOverflow: 'ellipsis',
    maxWidth: '100%'
  };

  export const TitleCSS: React.CSSProperties = {
    fontWeight: 'normal',
    fontSize: SpacesCommonStyles.TitleFontSize,
    margin: '0',
    marginRight: TabStyles.Spacing.ExtraLargeSpace,
    height: SpacesCommonStyles.RowHeight,
    lineHeight: SpacesCommonStyles.RowHeight,
  };

  export const Title = buildCss('title', truncated, TitleCSS);
  export const TitleV2 = buildCss('title-v2', truncated, TitleCSSV2);

  export const DetailIcons = cssClass(cssPrefix, 'detail-icons', {
    display: 'flex',
    marginRight: TabStyles.Spacing.ExtraLargeSpace,
    $nest: {
      '& > *': {
        marginRight: CommonStyles.Sizes.SmallSpace
      },
      '& > *:last-child': {
        marginRight: 0
      }
    }
  });

  export const EditableHeaderStylesV2 = cssClass(cssPrefix, 'editable-header', {
    overflow: 'visible',
    $nest: {
      '& > *': {
        marginBottom: 0,
        overflow: 'inherit',
      }
    }
  });

  export const EditableHeaderStyles = cssClass(cssPrefix, 'editable-header', {
    overflow: 'visible',
    $nest: {
      '& > *': {
        marginBottom: 0,
        overflow: 'inherit',
        alignItems: 'flex-start',
      }
    }
  });

  export const EditableNameRow = cssClass(cssPrefix, 'editable-name-row', {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    flexGrow: 1,
    height: EDITABLE_NAME_ROW_HEIGHT,
    $nest: {
      '&>*': {
        marginRight: TabStyles.Spacing.MediumSpace
      },
      '&>*:last-child': {
        marginRight: 0
      }
    }
  });

  export const EditableTitleCSSV2: React.CSSProperties = {
    ...TitleCSSV2,
    marginRight: '0'
  };

  export const EditableTitleCSS: React.CSSProperties = {
    ...TitleCSS,
    marginRight: '0'
  };

  export const EditableTitleInputOverridesV2: React.CSSProperties = {
    ...EditableTitleCSS,
    height: EDITABLE_NAME_ROW_HEIGHT,
    width: '100%',
  };

  export const EditableTitleInputOverrides: React.CSSProperties = {
    ...EditableTitleCSS,
    height: EDITABLE_NAME_ROW_HEIGHT,
    width: '100%',
  };

  export const EditableTitleInput = cssClass(cssPrefix, 'editable-title-input', {
    $nest: {
      '& *': {
        marginBottom: 0,
      }
    },
    display: 'flex !important', // override widget styling
  });

  export const EditableTitleReadonly = buildCss('editable-title-readonly', truncated, EditableTitleCSS, {
    padding: CommonStyles.Sizes.DefaultSpace,
  });

  export const CollectionTitle = cssClass(cssPrefix, 'collection-title', {
    $nest: {
      '& button': {
        marginLeft: CommonStyles.Sizes.MediumSpace,
      },
    },
  });

  export const HeaderEditableInlineElement = cssClass(cssPrefix, 'header-editable-inline-element', {
    $nest: {
      '& button': {
        opacity: 0,
      },
      '&:hover button': {
        opacity: 1,
      },
      '& button:focus': {
        opacity: 1,
      },
      '&:hover': {
        background: applyColor(DesignTokens.color.elevation.surface.default.subtle, 'rgb(245,245,245)'),
        cursor: 'pointer',
      }
    }
  });

  const HeaderInlineDescription: NestedCSSProperties = {
    fontSize: 14,
    lineHeight: '21px',
  };

  const inlineDescriptionSmall = 350;
  const inlineDescriptionMedium = 650;
  const inlineDescriptionBig = 700;
  const editButtonWidth = 20 + CommonStyles.Sizes.SmallSpace;

  export const InlineDescriptionReadonly = style(
    {
      whiteSpace: 'normal',
      padding: CommonStyles.Sizes.DefaultSpace,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      wordBreak: 'break-word',
      ...HeaderInlineDescription,
    },
    media({ maxWidth: BreakPoints.SMALL }, { maxWidth: inlineDescriptionSmall + editButtonWidth }),
    media({ minWidth: BreakPoints.SMALL + 1, maxWidth: BreakPoints.MEDIUM }, { maxWidth: inlineDescriptionMedium + editButtonWidth }),
    media({ minWidth: BreakPoints.MEDIUM + 1 }, { maxWidth: inlineDescriptionBig + editButtonWidth }),
  );

  export const InlineDescriptionEditResponsiveContainer = style(
    {
      display: 'block',
    },
    media({ maxWidth: BreakPoints.SMALL }, { maxWidth: inlineDescriptionSmall }),
    media({ minWidth: BreakPoints.SMALL + 1, maxWidth: BreakPoints.MEDIUM }, { maxWidth: inlineDescriptionMedium }),
    media({ minWidth: BreakPoints.MEDIUM + 1 }, { maxWidth: inlineDescriptionBig }),
  );

  export const HeaderInlineDescriptionTextArea = cssClass(cssPrefix, 'inline-description-text-area', {
    width: '100%',
    $nest: {
      '& textarea': {
        minHeight: 75,
        resize: 'none',
        ...HeaderInlineDescription,
      }
    }
  });

  export const ConfirmCancelButtons = cssClass(cssPrefix, 'confirm-cancel', {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginTop: CommonStyles.Sizes.SmallSpace,
    $nest: {
      '& > *': {
        marginLeft: CommonStyles.Sizes.MediumSpace
      }
    }
  });

  export const MetadataV2 = cssClass(cssPrefix, 'metadata', {
    fontWeight: 'normal',
    alignItems: 'center',
    display: 'inline-flex',
    verticalAlign: 'middle',
    justifyContent: 'center',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    fontSize: SpacesCommonStyles.SubtitleFontSize,
  });

  export const Metadata = cssClass(cssPrefix, 'metadata', {
    fontWeight: 'normal',
    marginTop: TabStyles.Spacing.SmallSpace,
    alignItems: 'center',
    display: 'inline-flex',
    verticalAlign: 'middle',
    justifyContent: 'center',
    flexDirection: 'row',
    fontSize: SpacesCommonStyles.SubtitleFontSize,
    flexWrap: 'nowrap',
  });

  // Compact Header Overrides
  export const CompactHeader = cssClass(cssPrefix, 'compact-header', {
    $nest: {
      [`& .${Metadata}`]: {
        marginTop: 'unset',
      },
      [`.${ExplorerStyles.ToolbarButtonGroup}>*`]: {
        margin: '0 3px'
      }
    }
  });

  export const MetadataContainer = cssClass(cssPrefix, 'metadata-container', {
    overflow: 'hidden',
  });

  export const TextLabelWithNoLeftMargin = cssClass(cssPrefix, 'text-label-no-left-padding',
    { ...MetadataStyles });

  export const UserLabel = cssClass(cssPrefix, 'userlabel',
    { ...MetadataStyles });

  export const ScheduleLabel = cssClass(cssPrefix, 'schedule-label',
    { ...MetadataStyles });

  export const GroupDateValue = cssClass(cssPrefix, 'group-label',
    {
      ...MetadataStyles,
      fontWeight: 'bold',
      marginLeft: TabStyles.Spacing.SmallSpace,
      marginRight: TabStyles.Spacing.LargeSpace
    });

  export const headerLinkStyle: React.CSSProperties = {
    ...noWrapText,
    fontWeight: 'bold',
  };

  export const headerLink = cssClass(cssPrefix, 'headerLink', headerLinkStyle);

  export const userEmailHeaderLink = cssClass(cssPrefix, 'user-email-header-link', {
    ...headerLinkStyle,
    marginLeft: TabStyles.Spacing.ExtraLargeSpace
  });

  const encryptionStatusNest: any = {};
  encryptionStatusNest[`& .${headerLink}`] = {
    textDecoration: 'none'
  };
  encryptionStatusNest[`& .disabled`] = {
    fontWeight: 'normal',
    color: applyColor(DesignTokens.color.font.default.secondary, CommonStyles.Colors.Grey102)
  };

  export const encryptionStatus = cssClass(cssPrefix, 'encryptionStatus', {
    display: 'inline-flex',
    marginLeft: TabStyles.Spacing.ExtraLargeSpace,
    marginRight: TabStyles.Spacing.DefaultSpace,
    $nest: encryptionStatusNest
  });

  export const ExtractStatus = cssClass(cssPrefix, 'extractStatus', {
    marginLeft: TabStyles.Spacing.ExtraLargeSpace,
    marginRight: TabStyles.Spacing.DefaultSpace,
  });

  export const ExtractStatusLabel = cssClass(cssPrefix, 'extractStatusLabel', {
    display: 'inline-block',
    marginLeft: TabStyles.Spacing.ExtraLargeSpace,
    marginRight: TabStyles.Spacing.DefaultSpace,
  });

  export const ExtractStatusSelection = cssClass(cssPrefix, 'extractStatusSelection', {
    display: 'inline-block',
    $nest: {
      '&>img': {
        position: 'fixed',
      }
    }
  });

  export const ExtractStatusSpinner = cssClass(cssPrefix, 'extractStatusSpinner', {
    display: 'inline-block',
    verticalAlign: 'sub',
    marginLeft: TabStyles.Spacing.DefaultSpace,
    marginRight: TabStyles.Spacing.DefaultSpace,
  });

  export const ExtractStatusText = cssClass(cssPrefix, 'extractStatusText', {
    display: 'inline-block',
    marginLeft: TabStyles.Spacing.DefaultSpace,
    marginRight: TabStyles.Spacing.DefaultSpace,
    $nest: encryptionStatusNest
  });

  export const DataLabel = cssClass(cssPrefix, 'datalabel',
    {
      ...MetadataStyles,
      marginLeft: TabStyles.Spacing.ExtraLargeSpace,
    });

  export const DataStatusIcon = cssClass(cssPrefix, 'dataStatusIcon', {
    display: 'flex',
  });

  export const TextOnlyMetadataLabelWithLeftMargin = cssClass(cssPrefix, 'textOnlyLabelWithLeftMargin', {
    ...MetadataStyles,
    ...noWrapText,
    marginLeft: TabStyles.Spacing.ExtraLargeSpace
  });

  export const MetadataInfo = cssClass(cssPrefix, 'metadatainfo', {
    ...MetadataStyles,
    fontWeight: 'bold',
  });

  export const CertificationIcon = cssClass(cssPrefix, 'icon', {
    marginLeft: TabStyles.Spacing.ExtraLargeSpace,
    height: '16px',
    width: '16px',
    position: 'relative',
    left: 0,
    top: '3px'
  });

  export const HeaderIcon = cssClass(cssPrefix, 'headerIcon', {
    marginLeft: TabStyles.Spacing.ExtraLargeSpace,
    height: '16px',
    width: '16px',
    position: 'relative',
    left: 0,
  });

  export const TextAndIconMetadataLabel = cssClass(cssPrefix, 'textAndIconMetadataLabel', {
    marginLeft: TabStyles.Spacing.SmallSpace,
    ...MetadataStyles,
  });

  export const ItemDescription = cssClass(cssPrefix, 'description', {
    fontWeight: 'normal',
    fontSize: TabStyles.Typography.Sizing.Default,
    color: applyColor(DesignTokens.color.font.default.secondary, TabStyles.Typography.Color.PrimaryLight),
    position: 'relative',
    whiteSpace: 'normal'
  });

  export const FavoriteTooltip = cssClass(cssPrefix, 'favoriteTooltip', {
    fontSize: SpacesCommonStyles.SubtitleFontSize,
    textAlign: 'center'
  });

  export const SpotlightActionButtonsRow = cssClass(cssPrefix, 'spotlightActionsRow', {
    marginTop: TabStyles.Spacing.MediumSpace
  });

  export const IconButtonStyleOverridesV2: CSSProperties = {
    height: 24,
    minWidth: 24,
    padding: 0,
  };

  export const IconButtonStyleOverrides: CSSProperties = {
    minWidth: 0,
    height: 24,
    width: 24,
    padding: 0,
  };

  export const InfoDeprecatedContent = cssClass(cssPrefix, 'InfoDeprecatedContent', {
    backgroundColor: applyColor(DesignTokens.color.elevation.surface.default.dark, 'tableau-grey-06'),
    paddingRight: TabStyles.Spacing.SmallSpace,
    paddingLeft: TabStyles.Spacing.SmallSpace,
  });

  export const InfoDetailImage = cssClass(cssPrefix, 'InfoDetailImage', {
    width: 18,
    height: 18,
    verticalAlign: 'middle',
  });
  export const HeaderButtonImage = cssClass(cssPrefix, 'HeaderButtonImage', {
    width: 18,
    height: 18,
    verticalAlign: 'middle',
  });
}
